import {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Award, Check, ChevronDown, Search, type LucideIcon } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ATTRIBUTION_MODELS,
  AttributionModel,
  attributionModelData,
  CHANNEL_COLORS,
  CHANNEL_KEY_MAP,
  Channel,
  ChannelType,
  CustomerInteraction,
  getFilteredCostRevenue,
  getFilteredCustomerInteractions,
  getFilteredFlowData,
  getFilteredFunnel,
  getFilteredHeatmap,
  getFilteredTimeSeries,
  getFilteredWeeklyConversions,
  getKPISummary,
  getVisibleChannels,
  TimePeriod,
} from "../data/attributionDashboard.data";
import {
  formatCompact,
  formatCompactCurrency,
  formatCurrency,
  formatNumber,
} from "../utils/attributionDashboard.utils";
import {
  DashboardDataTableSortButton,
  DashboardKpiCard,
  dashboardDataTable as dt,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardSectionHeader,
  DashboardDataTableTable,
  TablePagePagination,
  type DefinitionTooltipInfo,
} from "src/components/common";
import { getDashboardKpiLucideIconComponent } from "src/utils/dashboardKpiLucideIcon";
import { resolveDashboardChangePercent } from "src/utils/dashboardChangeFallback";
import { calculateBarWidthPct } from "src/utils/chartBarScale";
import "./AttributionDashboard.scss";

interface DashboardFilterState {
  timePeriod: TimePeriod;
  attributionModel: AttributionModel;
  channelType: ChannelType;
}

const timePeriodOptions: Array<{ label: string; value: TimePeriod }> = [
  { label: "Last 7 Days", value: "7d" },
  { label: "Last 30 Days", value: "30d" },
  { label: "Last 90 Days", value: "90d" },
  { label: "Last 12 Months", value: "12m" },
];

const modelOptions: Array<{ label: string; value: AttributionModel }> =
  ATTRIBUTION_MODELS.map(model => ({
    label: model,
    value: model,
  }));

const channelTypeOptions: Array<{ label: string; value: ChannelType }> = [
  { label: "All Channels", value: "all" },
  { label: "Paid Only", value: "paid" },
  { label: "Organic Only", value: "organic" },
];

const CONV_KEYS: Record<Channel, string> = {
  Instagram: "instagramConv",
  "Instagram Ads": "instagramAdsConv",
  Facebook: "facebookConv",
  "Facebook Ads": "facebookAdsConv",
  "Web Analytics (GA4)": "webGa4Conv",
  "Google Ads": "googleAdsConv",
};

const REV_KEYS: Record<Channel, string> = {
  Instagram: "instagramRev",
  "Instagram Ads": "instagramAdsRev",
  Facebook: "facebookRev",
  "Facebook Ads": "facebookAdsRev",
  "Web Analytics (GA4)": "webGa4Rev",
  "Google Ads": "googleAdsRev",
};

const SelectField = <T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: Dispatch<SetStateAction<T>>;
  options: Array<{ label: string; value: T }>;
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find(option => option.value === value) ?? options[0];

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  return (
    <div ref={rootRef} className="attribution-dashboard__select-menu">
      <button
        type="button"
        className={`attribution-dashboard__select-trigger ${
          open ? "attribution-dashboard__select-trigger--open" : ""
        }`}
        onClick={() => setOpen(current => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected.label}</span>
        <ChevronDown className="attribution-dashboard__select-icon" size={18} />
      </button>

      {open ? (
        <div className="attribution-dashboard__select-dropdown" role="listbox">
          {options.map(option => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={active}
                className={`attribution-dashboard__select-option ${
                  active ? "attribution-dashboard__select-option--active" : ""
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="attribution-dashboard__select-check">
                  {active ? <Check size={16} /> : null}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

type AttributionKpiCardConfig = {
  title: string;
  value: string;
  icon: LucideIcon;
  changePercent?: number;
  statusHint?: string;
};

const ATTRIBUTION_KPI_TOOLTIPS: Record<string, DefinitionTooltipInfo> = {
  "Total Conversions": {
    description:
      "Attributed conversions across channels for the selected time window and channel filter.",
  },
  "Total Revenue": {
    description: "Revenue attributed to marketing touchpoints in the selected period.",
  },
  "Avg ROAS": {
    description: "Average return on ad spend for paid activity in view.",
    formula: "Attributed revenue ÷ Ad spend",
  },
  "Top Channel": {
    description: "Channel with the strongest contribution by conversions under the current filters.",
  },
};

const KpiCards: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const { totalConversions, totalRevenue, avgRoas, topChannel } = getKPISummary(
    filters.timePeriod,
    filters.channelType
  );

  const cards: AttributionKpiCardConfig[] = [
    {
      title: "Total Conversions",
      value: formatNumber(totalConversions),
      changePercent: resolveDashboardChangePercent(undefined, "attribution-kpi-total-conversions"),
      icon: getDashboardKpiLucideIconComponent("Total Conversions"),
    },
    {
      title: "Total Revenue",
      value: formatCurrency(totalRevenue),
      changePercent: resolveDashboardChangePercent(undefined, "attribution-kpi-total-revenue"),
      icon: getDashboardKpiLucideIconComponent("Total Revenue"),
    },
    {
      title: "Avg ROAS",
      value: `${avgRoas.toFixed(2)}x`,
      changePercent: resolveDashboardChangePercent(undefined, "attribution-kpi-avg-roas"),
      icon: getDashboardKpiLucideIconComponent("Avg ROAS"),
    },
    {
      title: "Top Channel",
      value: topChannel,
      statusHint: "By conversions",
      icon: Award,
    },
  ];

  return (
    <div className="attribution-dashboard__kpi-grid">
      {cards.map(card => (
        <DashboardKpiCard
          key={card.title}
          className="attribution-dashboard__card attribution-dashboard__kpi-card"
          title={card.title}
          value={card.value}
          tooltip={ATTRIBUTION_KPI_TOOLTIPS[card.title]}
          metricLabelForTooltip={card.title}
          changePercent={card.changePercent}
          comparisonLabel="vs last period"
          description={card.statusHint}
          icon={<card.icon size={18} />}
        />
      ))}
    </div>
  );
};

const ChannelContributionCard: FC<{ filters: DashboardFilterState }> = ({
  filters,
}) => {
  const data = getFilteredWeeklyConversions(filters.timePeriod);
  const visible = getVisibleChannels(filters.channelType);

  return (
    <article className="attribution-dashboard__card">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">
          Channel Contribution to Conversions
        </h3>
        <p className="attribution-dashboard__card-subtitle">
          Weekly conversions by marketing channel
        </p>
      </div>
      <div className="attribution-dashboard__chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" />
            <XAxis dataKey="week" tick={{ fontSize: 12, fill: "#667085" }} />
            <YAxis tick={{ fontSize: 12, fill: "#667085" }} />
            <Tooltip />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="square"
              iconSize={10}
              wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
              formatter={(value, entry) => (
                <span style={{ color: entry.color ?? "#475467" }}>{value}</span>
              )}
            />
            {visible.map((channel, index) => (
              <Bar
                key={channel}
                dataKey={CHANNEL_KEY_MAP[channel]}
                name={channel}
                stackId="stack"
                fill={CHANNEL_COLORS[channel]}
                radius={index === visible.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
};

const AttributionModelCard: FC<{ filters: DashboardFilterState }> = ({
  filters,
}) => {
  const visible = getVisibleChannels(filters.channelType);
  const data = attributionModelData.map(row => ({
    ...row,
    isSelected: row.model === filters.attributionModel,
  }));

  return (
    <article className="attribution-dashboard__card">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">
          Attribution Model Comparison
        </h3>
        <p className="attribution-dashboard__card-subtitle">
          Conversion credit across models | {filters.attributionModel} selected
        </p>
      </div>
      <div className="attribution-dashboard__chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" />
            <XAxis dataKey="model" tick={{ fontSize: 11, fill: "#667085" }} />
            <YAxis tick={{ fontSize: 12, fill: "#667085" }} />
            <Tooltip />
            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: 12, paddingTop: 10 }}
              formatter={(value, entry) => (
                <span style={{ color: entry.color ?? "#475467" }}>{value}</span>
              )}
            />
            {visible.map(channel => (
              <Line
                key={channel}
                dataKey={CHANNEL_KEY_MAP[channel]}
                name={channel}
                stroke={CHANNEL_COLORS[channel]}
                strokeWidth={2}
                dot={{ r: 4 }}
                type="monotone"
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
};

const FunnelCard: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const data = getFilteredFunnel(filters.timePeriod, filters.channelType);
  const visible = getVisibleChannels(filters.channelType);

  const getChannelValue = (stage: (typeof data)[0], channel: Channel) =>
    stage[CHANNEL_KEY_MAP[channel]];
  const stageTotals = data.map(stage => visible.reduce((sum, ch) => sum + getChannelValue(stage, ch), 0));
  const maxTotal = Math.max(...stageTotals);
  const minTotal = Math.min(...stageTotals.filter(t => t > 0));

  return (
    <article className="attribution-dashboard__card">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">Customer Journey Funnel</h3>
        <p className="attribution-dashboard__card-subtitle">
          Impressions → Clicks → Conversions by channel
        </p>
      </div>
      <div className="attribution-dashboard__funnel">
        {data.map((stage, index) => {
          const total = visible.reduce(
            (sum, channel) => sum + getChannelValue(stage, channel),
            0
          );
          if (total === 0) {
            return null;
          }
          const widthPct = calculateBarWidthPct(total, maxTotal, minTotal, { mode: "linear" });
          const prevTotal =
            index > 0
              ? visible.reduce(
                  (sum, ch) => sum + getChannelValue(data[index - 1], ch),
                  0
                )
              : null;
          const dropOff =
            prevTotal && prevTotal > 0
              ? ((1 - total / prevTotal) * 100).toFixed(1)
              : null;

          return (
            <div key={stage.stage} className="attribution-dashboard__funnel-stage">
              <div className="attribution-dashboard__funnel-label-row">
                <span>{stage.stage}</span>
                <span>
                  {formatCompact(total)}
                  {dropOff ? (
                    <small className="attribution-dashboard__funnel-dropoff">
                      -{dropOff}%
                    </small>
                  ) : null}
                </span>
              </div>
              <div
                className="attribution-dashboard__funnel-bar"
                style={{ width: `${widthPct}%` }}
              >
                {visible.map(channel => {
                  const value = getChannelValue(stage, channel);
                  if (!value) {
                    return null;
                  }
                  const segmentPct = (value / total) * 100;
                  return (
                    <div
                      key={`${stage.stage}-${channel}`}
                      style={{
                        width: `${segmentPct}%`,
                        backgroundColor: CHANNEL_COLORS[channel],
                      }}
                      title={`${channel}: ${formatCompact(value)}`}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
        <div className="attribution-dashboard__legend-inline">
          {visible.map(channel => (
            <span key={channel}>
              <i style={{ backgroundColor: CHANNEL_COLORS[channel] }} />
              {channel}
            </span>
          ))}
        </div>
      </div>
    </article>
  );
};

const SankeyCard: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const flowData = getFilteredFlowData(filters.channelType);
  const width = 600;
  const height = 340;
  const nodeWidth = 18;
  const padding = 20;
  const innerW = width - padding * 2;
  const innerH = height - padding * 2;

  const nodeColor = (name: string) => {
    if (name === "Conversion") {
      return "#2f6edb";
    }
    return CHANNEL_COLORS[name as Channel] ?? "#667085";
  };

  const allNodes = new Set<string>();
  flowData.forEach(flow => {
    allNodes.add(flow.source);
    allNodes.add(flow.target);
  });

  const leftNodes = Array.from(allNodes).filter(
    node =>
      node !== "Conversion" &&
      !flowData.some(flow => flow.target === node && flow.source !== "Conversion")
  );
  const midNodes = Array.from(allNodes).filter(
    node =>
      node !== "Conversion" &&
      flowData.some(flow => flow.target === node && flow.source !== "Conversion")
  );
  const rightNodes = ["Conversion"].filter(node => allNodes.has(node));
  const columns = midNodes.length > 0 ? [leftNodes, midNodes, rightNodes] : [leftNodes, rightNodes];
  const colCount = columns.length;
  const colX = columns.map(
    (_, index) => padding + (innerW - nodeWidth) * (index / (colCount - 1 || 1))
  );

  const nodeValues: Record<string, number> = {};
  flowData.forEach(flow => {
    nodeValues[flow.source] = (nodeValues[flow.source] || 0) + flow.value;
    nodeValues[flow.target] = (nodeValues[flow.target] || 0) + flow.value;
  });

  const positions: Record<string, { x: number; y: number; h: number }> = {};
  const maxVal = Math.max(...Object.values(nodeValues), 1);

  columns.forEach((column, columnIndex) => {
    const totalH = column.reduce(
      (sum, name) => sum + Math.max((nodeValues[name] / maxVal) * innerH * 0.7, 30),
      0
    );
    const gap = (innerH - totalH) / (column.length + 1);
    let y = padding + gap;
    column.forEach(name => {
      const h = Math.max((nodeValues[name] / maxVal) * innerH * 0.7, 30);
      positions[name] = { x: colX[columnIndex], y, h };
      y += h + gap;
    });
  });

  const sourceOffsets: Record<string, number> = {};
  const targetOffsets: Record<string, number> = {};

  const flows = flowData
    .map(flow => {
      const src = positions[flow.source];
      const tgt = positions[flow.target];
      if (!src || !tgt) {
        return null;
      }

      const srcTotal = nodeValues[flow.source];
      const tgtTotal = nodeValues[flow.target];
      const flowH = (flow.value / srcTotal) * src.h;
      const flowHTarget = (flow.value / tgtTotal) * tgt.h;

      const srcOff = sourceOffsets[flow.source] || 0;
      const tgtOff = targetOffsets[flow.target] || 0;
      sourceOffsets[flow.source] = srcOff + flowH;
      targetOffsets[flow.target] = tgtOff + flowHTarget;

      const x1 = src.x + nodeWidth;
      const y1 = src.y + srcOff;
      const x2 = tgt.x;
      const y2 = tgt.y + tgtOff;
      const midX = (x1 + x2) / 2;

      return {
        ...flow,
        path: `M${x1},${y1} C${midX},${y1} ${midX},${y2} ${x2},${y2} L${x2},${y2 + flowHTarget} C${midX},${y2 + flowHTarget} ${midX},${y1 + flowH} ${x1},${y1 + flowH} Z`,
        color: nodeColor(flow.source),
      };
    })
    .filter(Boolean);

  return (
    <article className="attribution-dashboard__card">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">Channel Interaction Flow</h3>
        <p className="attribution-dashboard__card-subtitle">
          User movement between touchpoints before conversion
        </p>
      </div>
      <div className="attribution-dashboard__sankey-wrap">
        <svg viewBox={`0 0 ${width} ${height}`} className="attribution-dashboard__sankey">
          {flows.map((flow, index) => {
            if (!flow) {
              return null;
            }
            return (
              <path
                key={`${flow.source}-${flow.target}-${index}`}
                d={flow.path}
                fill={flow.color}
                opacity={0.35}
              >
                <title>{`${flow.source} -> ${flow.target}: ${flow.value.toLocaleString()}`}</title>
              </path>
            );
          })}
          {Object.entries(positions).map(([name, pos]) => (
            <g key={name}>
              <rect x={pos.x} y={pos.y} width={nodeWidth} height={pos.h} rx={3} fill={nodeColor(name)} />
              <text
                x={name === "Conversion" ? pos.x - 6 : pos.x + nodeWidth + 6}
                y={pos.y + pos.h / 2}
                textAnchor={name === "Conversion" ? "end" : "start"}
                dominantBaseline="middle"
                className="attribution-dashboard__sankey-label"
              >
                {name}
              </text>
            </g>
          ))}
        </svg>
      </div>
    </article>
  );
};

const TimeSeriesCard: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const data = getFilteredTimeSeries(filters.timePeriod);
  const visible = getVisibleChannels(filters.channelType);

  return (
    <article className="attribution-dashboard__card attribution-dashboard__card--full">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">
          Conversion and Revenue Over Time
        </h3>
        <p className="attribution-dashboard__card-subtitle">Monthly trends by channel</p>
      </div>
      <div className="attribution-dashboard__dual-chart">
        <div>
          <p className="attribution-dashboard__axis-title">Conversions</p>
          <div className="attribution-dashboard__chart attribution-dashboard__chart--small">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#667085" }} />
                <YAxis tick={{ fontSize: 12, fill: "#667085" }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {visible.map(channel => (
                  <Line
                    key={`conv-${channel}`}
                    dataKey={CONV_KEYS[channel]}
                    name={channel}
                    stroke={CHANNEL_COLORS[channel]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    type="monotone"
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div>
          <p className="attribution-dashboard__axis-title">Revenue</p>
          <div className="attribution-dashboard__chart attribution-dashboard__chart--small">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" />
                <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#667085" }} />
                <YAxis
                  tick={{ fontSize: 12, fill: "#667085" }}
                  tickFormatter={value => formatCompactCurrency(Number(value))}
                />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {visible.map(channel => (
                  <Line
                    key={`rev-${channel}`}
                    dataKey={REV_KEYS[channel]}
                    name={channel}
                    stroke={CHANNEL_COLORS[channel]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    type="monotone"
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </article>
  );
};

const CostRevenueCard: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const data = getFilteredCostRevenue(filters.channelType);

  return (
    <article className="attribution-dashboard__card">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">Cost vs Revenue / ROAS</h3>
        <p className="attribution-dashboard__card-subtitle">
          Spend compared to revenue with ROAS overlay
        </p>
      </div>
      <div className="attribution-dashboard__chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e4e9f2" />
            <XAxis
              dataKey="channel"
              tick={{ fontSize: 11, fill: "#667085" }}
              tickFormatter={value =>
                value === "Web Analytics (GA4)" ? "GA4" : String(value)
              }
            />
            <YAxis
              yAxisId="money"
              tick={{ fontSize: 12, fill: "#667085" }}
              tickFormatter={value => formatCompactCurrency(Number(value))}
            />
            <YAxis
              yAxisId="roas"
              orientation="right"
              tick={{ fontSize: 12, fill: "#667085" }}
              tickFormatter={value => `${value}x`}
            />
            <Tooltip
              formatter={(value: number, name: string) => {
                if (name === "ROAS") {
                  return `${value.toFixed(2)}x`;
                }
                return formatCurrency(value);
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="money" dataKey="cost" name="Cost" fill="#d9534f" opacity={0.7} radius={[4, 4, 0, 0]} />
            <Bar yAxisId="money" dataKey="revenue" name="Revenue" fill="#2f6edb" opacity={0.8} radius={[4, 4, 0, 0]} />
            <Line
              yAxisId="roas"
              dataKey="roas"
              name="ROAS"
              stroke="#f97316"
              strokeWidth={3}
              dot={{ r: 6, fill: "#f97316", stroke: "#f97316", strokeWidth: 1 }}
              activeDot={{ r: 7, fill: "#f97316", stroke: "#f97316" }}
              type="monotone"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </article>
  );
};

const HeatmapCard: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const visible = getVisibleChannels(filters.channelType);
  const data = getFilteredHeatmap(filters.channelType);
  const shortName = (channel: string) =>
    channel.replace(" (GA4)", "").replace("Web Analytics", "GA4");

  const getHeatClass = (impact: number) => {
    if (impact >= 80) {
      return "attribution-dashboard__heat-cell--level-5";
    }
    if (impact >= 60) {
      return "attribution-dashboard__heat-cell--level-4";
    }
    if (impact >= 45) {
      return "attribution-dashboard__heat-cell--level-3";
    }
    if (impact >= 30) {
      return "attribution-dashboard__heat-cell--level-2";
    }
    return "attribution-dashboard__heat-cell--level-1";
  };

  return (
    <article className="attribution-dashboard__card">
      <div className="attribution-dashboard__card-head">
        <h3 className="attribution-dashboard__card-title">Cross-Channel Impact</h3>
        <p className="attribution-dashboard__card-subtitle">
          Conversion impact score for channel combinations
        </p>
      </div>
      <div className="attribution-dashboard__heatmap-wrap">
        <table className="attribution-dashboard__heatmap">
          <thead>
            <tr>
              <th>
                Source ↓ / Target
                <br />
                →
              </th>
              {visible.map(channel => (
                <th key={channel}>{shortName(channel)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map(source => (
              <tr key={source}>
                <td>{shortName(source)}</td>
                {visible.map(target => {
                  const cell = data.find(
                    row => row.source === source && row.target === target
                  );
                  const impact = cell?.impact ?? 0;
                  return (
                    <td key={`${source}-${target}`}>
                      <div className={`attribution-dashboard__heat-cell ${getHeatClass(impact)}`}>
                        {impact}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
};

const ATTRIBUTION_CUSTOMER_PAGE_SIZE = 10;

type CustomerTableSortKey =
  | "customerId"
  | "channel"
  | "timestamp"
  | "touchpoint"
  | "conversionStatus"
  | "revenue";

function compareCustomerInteraction(
  a: CustomerInteraction,
  b: CustomerInteraction,
  key: CustomerTableSortKey,
  dir: "asc" | "desc"
): number {
  const inv = dir === "desc" ? -1 : 1;
  if (key === "revenue") {
    const an = a.revenue;
    const bn = b.revenue;
    if ((an === null || an === undefined) && (bn === null || bn === undefined)) {
      return 0;
    }
    if (an === null || an === undefined) {
      return 1;
    }
    if (bn === null || bn === undefined) {
      return -1;
    }
    return (an - bn) * inv;
  }
  const sa = String(a[key]);
  const sb = String(b[key]);
  return sa.localeCompare(sb) * inv;
}

const CustomerTableCard: FC<{ filters: DashboardFilterState }> = ({ filters }) => {
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<CustomerTableSortKey>("timestamp");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const data = getFilteredCustomerInteractions(filters.channelType);

  const filtered = useMemo(() => {
    if (!search) {
      return data;
    }
    const query = search.toLowerCase();
    return data.filter(
      row =>
        row.customerId.toLowerCase().includes(query) ||
        row.channel.toLowerCase().includes(query) ||
        row.touchpoint.toLowerCase().includes(query)
    );
  }, [data, search]);

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) =>
        compareCustomerInteraction(a, b, sortKey, sortDir)
      ),
    [filtered, sortKey, sortDir]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(sorted.length / ATTRIBUTION_CUSTOMER_PAGE_SIZE)
  );
  const currentPage = Math.min(page, totalPages);

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * ATTRIBUTION_CUSTOMER_PAGE_SIZE;
    return sorted.slice(start, start + ATTRIBUTION_CUSTOMER_PAGE_SIZE);
  }, [sorted, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [search, filters.timePeriod, filters.attributionModel, filters.channelType]);

  useEffect(() => {
    setPage(p => Math.min(p, totalPages));
  }, [totalPages]);

  const toggleSort = (key: CustomerTableSortKey) => {
    setPage(1);
    if (sortKey === key) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "timestamp" ? "desc" : "asc");
    }
  };

  const tableClass = `${dt.tableCompact} attribution-dashboard__table`.trim();

  return (
    <article className="attribution-dashboard__card attribution-dashboard__card--full attribution-dashboard__card--analytics-flat">
      <DashboardDataTablePanel className="attribution-dashboard__data-table-panel">
        <DashboardDataTablePanelHeader>
          <div className="attribution-dashboard__panel-header-text">
            <h3>Customer Attribution Table</h3>
            <p className="attribution-dashboard__card-subtitle">
              Individual interactions by channel, timestamp and conversion status
            </p>
          </div>
          <label className={dt.search}>
            <Search size={16} />
            <input
              type="search"
              placeholder="Search customer ID..."
              value={search}
              onChange={event => setSearch(event.target.value)}
            />
          </label>
        </DashboardDataTablePanelHeader>
        <DashboardDataTableScroll className="attribution-dashboard__table-wrap">
          <DashboardDataTableTable className={tableClass}>
            <thead>
              <tr>
                <th>
                  <DashboardDataTableSortButton
                    onClick={() => toggleSort("customerId")}
                  >
                    Customer ID
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton
                    onClick={() => toggleSort("channel")}
                  >
                    Channel
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton
                    onClick={() => toggleSort("timestamp")}
                  >
                    Timestamp
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton
                    onClick={() => toggleSort("touchpoint")}
                  >
                    Touchpoint
                  </DashboardDataTableSortButton>
                </th>
                <th>
                  <DashboardDataTableSortButton
                    onClick={() => toggleSort("conversionStatus")}
                  >
                    Status
                  </DashboardDataTableSortButton>
                </th>
                <th className={dt.number}>
                  <DashboardDataTableSortButton
                    onClick={() => toggleSort("revenue")}
                  >
                    Revenue
                  </DashboardDataTableSortButton>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row, index) => (
                <tr key={`${row.customerId}-${row.timestamp}-${index}`}>
                  <td className={`${dt.cellEmphasis} attribution-dashboard__mono`.trim()}>
                    {row.customerId}
                  </td>
                  <td>
                    <span className="attribution-dashboard__channel-dot-wrap">
                      <i style={{ backgroundColor: CHANNEL_COLORS[row.channel] }} />
                      {row.channel}
                    </span>
                  </td>
                  <td>{row.timestamp}</td>
                  <td>
                    <span
                      className={`attribution-dashboard__badge ${
                        row.touchpoint === "Conversion"
                          ? "attribution-dashboard__badge--primary"
                          : ""
                      }`}
                    >
                      {row.touchpoint}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`attribution-dashboard__badge ${
                        row.conversionStatus === "Converted"
                          ? "attribution-dashboard__badge--success"
                          : "attribution-dashboard__badge--outline"
                      }`}
                    >
                      {row.conversionStatus}
                    </span>
                  </td>
                  <td className={dt.number}>
                    {row.revenue !== null && row.revenue !== undefined ? (
                      formatCurrency(row.revenue)
                    ) : (
                      <span className={dt.empty}>—</span>
                    )}
                  </td>
                </tr>
              ))}
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="attribution-dashboard__empty">
                    No interactions found
                  </td>
                </tr>
              ) : null}
            </tbody>
          </DashboardDataTableTable>
        </DashboardDataTableScroll>
        <TablePagePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPrevious={() => setPage(p => Math.max(1, p - 1))}
          onNext={() => setPage(p => Math.min(totalPages, p + 1))}
        />
      </DashboardDataTablePanel>
    </article>
  );
};

const AttributionDashboard: FC = () => {
  const [timePeriod, setTimePeriod] = useState<TimePeriod>("30d");
  const [attributionModel, setAttributionModel] =
    useState<AttributionModel>("Multi-Touch");
  const [channelType, setChannelType] = useState<ChannelType>("all");

  const filters: DashboardFilterState = {
    timePeriod,
    attributionModel,
    channelType,
  };

  return (
    <div className="attribution-dashboard">
      <DashboardSectionHeader
        className="attribution-dashboard__header"
        title="Attribution Dashboard"
        subtitle="Customer journey and channel performance"
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <>
            <div className="dashboard-section-header__control">
              <SelectField
                value={timePeriod}
                onChange={setTimePeriod}
                options={timePeriodOptions}
              />
            </div>
            <div className="dashboard-section-header__control">
              <SelectField
                value={attributionModel}
                onChange={setAttributionModel}
                options={modelOptions}
              />
            </div>
            <div className="dashboard-section-header__control">
              <SelectField
                value={channelType}
                onChange={setChannelType}
                options={channelTypeOptions}
              />
            </div>
          </>
        }
      />

      <main className="attribution-dashboard__main">
        <KpiCards filters={filters} />
        <div className="attribution-dashboard__grid">
          <ChannelContributionCard filters={filters} />
          <AttributionModelCard filters={filters} />
        </div>
        <div className="attribution-dashboard__grid">
          <FunnelCard filters={filters} />
          <SankeyCard filters={filters} />
        </div>
        <TimeSeriesCard filters={filters} />
        <div className="attribution-dashboard__grid">
          <CostRevenueCard filters={filters} />
          <HeatmapCard filters={filters} />
        </div>
        <CustomerTableCard filters={filters} />
      </main>
    </div>
  );
};

export default AttributionDashboard;
